import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { 
  Rocket, 
  Package, 
  Users, 
  CreditCard, 
  Mail, 
  Share2,
  ArrowRight,
  Sparkles,
  CheckCircle2
} from "lucide-react";

const WelcomeModal = ({ user, onClose }) => {
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has seen the welcome modal before
    const hasSeenWelcome = localStorage.getItem(`arawol_welcome_${user?.user_id}`);
    
    // Only show for admin users who haven't seen it
    if (user?.role === "admin" && !hasSeenWelcome) {
      // Small delay for better UX
      const timer = setTimeout(() => setOpen(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(`arawol_welcome_${user?.user_id}`, "true");
    }
    setOpen(false);
    onClose?.();
  };

  const goToSetupGuide = () => {
    localStorage.setItem(`arawol_welcome_${user?.user_id}`, "true");
    setOpen(false);
    // Navigate to admin with setup guide tab
    // The tab will need to be activated manually or via URL param
    onClose?.("quickstart");
  };

  const steps = [
    {
      title: "Welcome to Art By Arawol! 🎨",
      description: "Your Aboriginal art wholesale marketplace is ready. Let's get you set up!",
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Package className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-sm">10 Products</p>
                <p className="text-xs text-muted-foreground">Ready to sell</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-sm">Multi-Role</p>
                <p className="text-xs text-muted-foreground">Wholesale & Retail</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <CreditCard className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-sm">Stripe Ready</p>
                <p className="text-xs text-muted-foreground">Accept payments</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Share2 className="w-5 h-5 text-purple-600" />
              <div>
                <p className="font-medium text-sm">Marketing Suite</p>
                <p className="text-xs text-muted-foreground">AI-powered</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Complete Your Setup",
      description: "A few quick configurations will unlock all features:",
      icon: Rocket,
      content: (
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Email Notifications</p>
              <p className="text-xs text-muted-foreground">Send order confirmations, shipping updates & wholesale approvals</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-4 h-4 text-yellow-600" />
            </div>
            <div>
              <p className="font-medium text-sm">PayPal Payments</p>
              <p className="text-xs text-muted-foreground">Accept PayPal as an additional payment method</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Share2 className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Social Media Publishing</p>
              <p className="text-xs text-muted-foreground">Post directly to Twitter, Facebook, Instagram & more</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "You're All Set! 🎉",
      description: "Head to the Setup Guide for step-by-step instructions:",
      icon: CheckCircle2,
      content: (
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
            <Rocket className="w-10 h-10 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">
              The <strong>Setup Guide</strong> tab in your dashboard has detailed instructions for configuring each integration.
            </p>
          </div>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700">
              <strong>Pro tip:</strong> Start with Email notifications - it only takes 5 minutes to set up!
            </p>
          </div>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const StepIcon = currentStepData.icon;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="welcome-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
              <StepIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">{currentStepData.title}</DialogTitle>
              <DialogDescription>{currentStepData.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          {currentStepData.content}
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 py-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentStep ? "bg-amber-500" : "bg-slate-200"
              }`}
            />
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          {currentStep === steps.length - 1 ? (
            <>
              <div className="flex items-center gap-2 mr-auto">
                <Checkbox
                  id="dontShow"
                  checked={dontShowAgain}
                  onCheckedChange={setDontShowAgain}
                />
                <label htmlFor="dontShow" className="text-sm text-muted-foreground cursor-pointer">
                  Don't show again
                </label>
              </div>
              <Button variant="outline" onClick={handleClose}>
                Maybe Later
              </Button>
              <Button 
                onClick={goToSetupGuide}
                className="bg-amber-600 hover:bg-amber-700 gap-2"
              >
                <Rocket className="w-4 h-4" />
                Go to Setup Guide
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={handleClose} className="mr-auto">
                Skip
              </Button>
              <Button 
                onClick={() => setCurrentStep(prev => prev + 1)}
                className="gap-2"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeModal;
